import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Suspense, useCallback } from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/web/components/primitives/tabs";

import { BrandsTab } from "./-tabs/BrandsTab";
import { BrowseMapTab } from "./-tabs/BrowseMapTab";
import { CardsTab } from "./-tabs/CardsTab";
import { ConnectionsTab } from "./-tabs/ConnectionsTab";
import { FeatureFlagsTab } from "./-tabs/FeatureFlagsTab";
import { InvitedBrandsTab } from "./-tabs/InvitedBrandsTab";
import { MigrationsTab } from "./-tabs/MigrationsTab";
import { NominationClaimsTab } from "./-tabs/NominationClaimsTab";
import { PropertiesTab } from "./-tabs/PropertiesTab";
import { QuestionsTab } from "./-tabs/QuestionsTab";
import { SecretsTab } from "./-tabs/SecretsTab";
import { TransactTab } from "./-tabs/TransactTab";
import { TtwPagesTab } from "./-tabs/TtwPagesTab";
import { UserInfoTab } from "./-tabs/UserInfoTab";
import { WorkflowsTab } from "./-tabs/WorkflowsTab";

const ADMIN_TABS = [
  "brands",
  "invitedBrands",
  "workflows",
  "connections",
  "cards",
  "nominationClaims",
  "userInfo",
  "migrations",
  "properties",
  "browseMap",
  "secrets",
  "featureFlags",
  "questions",
  "transact",
  "ttwPages",
] as const;

type AdminTab = (typeof ADMIN_TABS)[number];

const DEFAULT_TAB: AdminTab = "brands";

const VALID_TABS = new Set<AdminTab>(ADMIN_TABS);

function isAdminTab(value: unknown): value is AdminTab {
  return typeof value === "string" && VALID_TABS.has(value as AdminTab);
}

export const Route = createFileRoute("/_protected/admin/")({
  component: RouteComponent,
  validateSearch: (raw: Record<string, unknown>): { tab?: AdminTab } => ({
    tab: isAdminTab(raw.tab) ? raw.tab : undefined,
  }),
  // Keep the address bar self-describing: a bare /admin (or an unrecognized tab)
  // is normalized to the default tab before the page renders, so the URL is
  // always shareable as-is.
  beforeLoad: ({ search }) => {
    if (!search.tab) {
      throw redirect({ to: "/admin", search: { tab: DEFAULT_TAB }, replace: true });
    }
  },
});

function RouteComponent() {
  const { tab = DEFAULT_TAB } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const setTab = useCallback(
    (value: string) => {
      if (!isAdminTab(value)) return;
      void navigate({ search: { tab: value }, replace: true });
    },
    [navigate],
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      <h1 className="text-lg font-semibold text-withus-dark-blue">Admin Dashboard</h1>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="brands">Brands</TabsTrigger>
          <TabsTrigger value="invitedBrands">Invited brands</TabsTrigger>
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="connections">Connections</TabsTrigger>
          <TabsTrigger value="cards">Cards</TabsTrigger>
          <TabsTrigger value="nominationClaims">Nomination claims</TabsTrigger>
          <TabsTrigger value="userInfo">User Info</TabsTrigger>
          <TabsTrigger value="migrations">Migrations</TabsTrigger>
          <TabsTrigger value="properties">Properties</TabsTrigger>
          <TabsTrigger value="browseMap">Browse map</TabsTrigger>
          <TabsTrigger value="secrets">Secrets</TabsTrigger>
          <TabsTrigger value="featureFlags">Feature flags</TabsTrigger>
          <TabsTrigger value="questions">Questions</TabsTrigger>
          <TabsTrigger value="transact">Transact</TabsTrigger>
          <TabsTrigger value="ttwPages">TTW pages</TabsTrigger>
        </TabsList>

        <Suspense fallback={<div>Loading...</div>}>
          <TabsContent value="brands">
            <BrandsTab />
          </TabsContent>
          <TabsContent value="invitedBrands">
            <InvitedBrandsTab />
          </TabsContent>
          <TabsContent value="workflows">
            <WorkflowsTab />
          </TabsContent>
          <TabsContent value="connections">
            <ConnectionsTab />
          </TabsContent>
          <TabsContent value="cards">
            <CardsTab />
          </TabsContent>
          <TabsContent value="nominationClaims">
            <NominationClaimsTab />
          </TabsContent>
          <TabsContent value="userInfo">
            <UserInfoTab />
          </TabsContent>
          <TabsContent value="migrations">
            <MigrationsTab />
          </TabsContent>
          <TabsContent value="properties">
            <PropertiesTab />
          </TabsContent>
          <TabsContent value="browseMap">
            <BrowseMapTab />
          </TabsContent>
          <TabsContent value="secrets">
            <SecretsTab />
          </TabsContent>
          <TabsContent value="featureFlags">
            <FeatureFlagsTab />
          </TabsContent>
          <TabsContent value="questions">
            <QuestionsTab />
          </TabsContent>
          <TabsContent value="transact">
            <TransactTab />
          </TabsContent>
          <TabsContent value="ttwPages">
            <TtwPagesTab />
          </TabsContent>
        </Suspense>
      </Tabs>
    </div>
  );
}
